import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Form, FormGenerator } from '@/shared/ui';
import { DialogAlert } from '@/shared/ui/DialogAlert';
import { TFormMutation } from '@/shared/ui/Form';
import { FormFieldValues } from '@/shared/ui/FormGenerator';
import { CustomTypography } from '@/shared/ui/Typography';

import { telegramPersonalPhoneFormDataSchema } from '../../model/schemas';
import { useInitSms } from '../../model/telegramPersonal/useInitSms';
import { WarningTextAboutDialogs } from '../scanQrCode/WarningTextAboutDialogs';

import { useTelegramAuth } from './TelegramAuthContext';

type TTelegramPersonalPhoneFormValues = {
  phone: string;
};

interface ITelegramPersonalPhoneFormProps {
  mutation?: TFormMutation<void>;
  channelId: string;
  setPhone: React.Dispatch<React.SetStateAction<string>>;
  defaultValues?: TTelegramPersonalPhoneFormValues;
}

export const TELEGRAM_PERSONAL_PHONE_FORM_ID = 'telegram-personal-phone-form';

export const TelegramPersonalPhoneForm = ({
  mutation,
  channelId,
  setPhone,
  defaultValues = { phone: '' },
}: ITelegramPersonalPhoneFormProps) => {
  const { t } = useTranslation();

  const { setIsLoading, applyAuthResult } = useTelegramAuth();
  const { initSms, isPending } = useInitSms();

  useEffect(() => {
    setIsLoading(isPending);
  }, [isPending, setIsLoading]);

  const formScheme = [
    {
      name: 'phone',
      label: t('common:inputs.phoneNumber'),
      type: FormFieldValues.Phone,
      inputProps: { fullWidth: false },
    },
  ];

  const handleSubmit = async (data: { phone: string }) => {
    const result = await initSms({ channelId, phone: data.phone });
    applyAuthResult(result);
    setPhone(data.phone);
  };

  return (
    <Box sx={{ maxWidth: '600px' }}>
      <DialogAlert sx={{ mb: '25px' }}>
        {t('channels:tgPersonal.authByPhone.alert')}
      </DialogAlert>
      <Form
        id={TELEGRAM_PERSONAL_PHONE_FORM_ID}
        schema={telegramPersonalPhoneFormDataSchema(t)}
        onSubmit={handleSubmit}
        defaultValues={defaultValues}
        mode="onChange"
        mutation={mutation}
      >
        <Box sx={{ mb: '20px' }}>
          <CustomTypography variant="body1">
            {t('channels:tgPersonal.phoneAuthDescription.part1')}
          </CustomTypography>
          <CustomTypography variant="body1">
            {t('channels:tgPersonal.phoneAuthDescription.part2')}
          </CustomTypography>
        </Box>
        <FormGenerator formScheme={formScheme} />
        <Box sx={{ mt: '20px' }}>
          <WarningTextAboutDialogs />
        </Box>
      </Form>
    </Box>
  );
};
